import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { SubscriptionData, SubscriptionPlan } from "@/config/stripe";

export const useSubscription = () => {
  const { user, session } = useAuth();
  const queryClient = useQueryClient();

  const {
    data: subscription,
    isLoading,
    error,
    refetch: checkSubscription,
  } = useQuery({
    queryKey: ["subscription", user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      // First try: Query by user_id (primary method)
      const { data: localSub } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      
      // If we have local data by user_id, return it
      if (localSub) {
        return {
          subscribed: true,
          plan: localSub.plan as SubscriptionPlan,
          billing_cycle: localSub.billing_cycle as SubscriptionData["billing_cycle"],
          status: localSub.status,
          current_period_end: localSub.current_period_end,
          cancel_at_period_end: localSub.cancel_at_period_end,
        } as SubscriptionData;
      }
      
      // Fallback: Query by email via profiles join (handles identity linking issues)
      // This covers cases where user signed up with Google, then added email login
      if (user.email) {
        const { data: emailSub } = await supabase
          .from("subscriptions")
          .select(`
            *,
            profiles!subscriptions_user_id_fkey(email)
          `)
          .eq("profiles.email", user.email)
          .maybeSingle();
        
        if (emailSub) {
          return {
            subscribed: true,
            plan: emailSub.plan as SubscriptionPlan,
            billing_cycle: emailSub.billing_cycle as SubscriptionData["billing_cycle"],
            status: emailSub.status,
            current_period_end: emailSub.current_period_end,
            cancel_at_period_end: emailSub.cancel_at_period_end,
          } as SubscriptionData;
        }
      }
      
      // No subscription found = free user
      return { subscribed: false, plan: "free" as SubscriptionPlan, status: "none" } as SubscriptionData;
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Quick refresh - only reads from local database, very fast
  const quickRefresh = async () => {
    if (!user) return null;
    
    // First try: by user_id
    let localSub = null;
    let localError = null;
    
    const result1 = await supabase
      .from("subscriptions")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();
    
    localSub = result1.data;
    localError = result1.error;
    
    // Fallback: by email via profiles join
    if (!localSub && !localError && user.email) {
      const result2 = await supabase
        .from("subscriptions")
        .select(`
          *,
          profiles!subscriptions_user_id_fkey(email)
        `)
        .eq("profiles.email", user.email)
        .maybeSingle();
      
      localSub = result2.data;
    }
    
    if (localSub) {
      const newData: SubscriptionData = {
        subscribed: true,
        plan: localSub.plan as SubscriptionPlan,
        billing_cycle: localSub.billing_cycle as SubscriptionData["billing_cycle"],
        status: localSub.status,
        current_period_end: localSub.current_period_end,
        cancel_at_period_end: localSub.cancel_at_period_end,
      };
      
      // Update React Query cache directly
      queryClient.setQueryData(["subscription", user.id], newData);
      return newData;
    }
    
    // No local data, return free plan
    const freeData: SubscriptionData = {
      subscribed: false,
      plan: "free",
      status: "none",
    };
    queryClient.setQueryData(["subscription", user.id], freeData);
    return freeData;
  };

  const createCheckout = async (priceId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId },
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (err) {
      console.error("Checkout creation failed:", err);
      throw err;
    }
  };

  const openCustomerPortal = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (err) {
      console.error("Customer portal failed:", err);
      throw err;
    }
  };

  const currentPlan: SubscriptionPlan = subscription?.plan || "free";
  const isSubscribed = subscription?.subscribed || false;
  const isPro = currentPlan === "pro";
  const isBasic = currentPlan === "basic";
  const isFree = currentPlan === "free";

  return {
    subscription: subscription || null,
    isLoading,
    error: error ? (error instanceof Error ? error.message : "Failed to check subscription") : null,
    currentPlan,
    isSubscribed,
    isPro,
    isBasic,
    isFree,
    checkSubscription,
    quickRefresh,
    createCheckout,
    openCustomerPortal,
  };
};
